import { useTranslation } from 'next-i18next'

import ResponsiveImage from '~/components/Image/ResponsiveImage'
import ROUTES from '~/shared/constants/routes.const'
import Spinner from '~/components/Spinner'
import { Link } from '~/components'
import useCurrentUser from '~/hooks/useCurrentUser'
import { ButtonPrimary } from '~/components/Buttons'
import { DEFAULT_AVATAR_URL } from '~/shared/constants/assets.const'
import { useGetAvatarMetadataQuery } from '~/redux/apis/user-api'
import { OutfitGender } from '~/homebase/types/Homebase.types'
import { IUser } from '~/types/user-info-types'

import useAvatarImageUrl from '../hooks/useAvatarImageUrl'

export default function DashboardAvatarImage(): React.ReactElement {
  const { t } = useTranslation('avatar')
  const avatarImageObject = useAvatarImageUrl()
  const user = useCurrentUser()
  const { data: metadata } = useGetAvatarMetadataQuery(user as IUser, { skip: !user?.avatarUrl })

  if (avatarImageObject.isLoading) {
    return (
      <div className="flex justify-center items-center h-16">
        <Spinner size={30} />
      </div>
    )
  }

  if (avatarImageObject.isDeprecatedUrl || !avatarImageObject.isExists) {
    return (
      <div className="relative flex flex-col justify-center items-center py-1">
        <div className="relative border-2 border-purple rounded-full w-18 overflow-hidden">
          <ResponsiveImage
            alt={`${user?.nickname} avatar`}
            className="w-full h-full"
            height={114}
            imageClassName="bg-white rounded-full"
            srcKey={DEFAULT_AVATAR_URL}
            width={114}
          />
          <div className="absolute inset-0 bg-gray-placeholder bg-opacity-50" />
        </div>
        {avatarImageObject.isDeprecatedUrl && (
          <div className="relative flex flex-col justify-center items-center">
            <ResponsiveImage
              alt={`${user?.nickname} avatar`}
              className="p-1 rounded-full w-16"
              height={114}
              imageClassName="bg-white rounded-full"
              srcKey="icons/warning.svg"
              width={114}
            />
            <p className="font-medium text-white text-sm">{t('outdatedAvatar')}</p>
          </div>
        )}
        <div className="top-3 z-40 absolute hover:scale-110 duration-300 transform">
          <div className="flex justify-center">
            <Link className="inline-block" to={ROUTES.AVATAR}>
              <ResponsiveImage
                alt={t('editAvatar')}
                height={60}
                imageClassName="bg-purple-dark rounded-full py-1 pl-1 pr-0.5 border-2 border-white w-8"
                srcKey="icons/edit-avatar.svg"
                width={70}
              />
            </Link>
          </div>
          <ButtonPrimary
            className="my-1 px-4 py-1 border !rounded-2 text-white text-xxs cursor-pointer"
            disableRing
            href={ROUTES.AVATAR}
          >
            {t('createAvatarButton')}
          </ButtonPrimary>
        </div>
      </div>
    )
  }

  return (
    <div className="z-10 relative flex flex-col justify-start items-start m-auto p-0.5 border-2 border-white rounded-full w-22 h-22">
      <div className="bg-white m-auto rounded-full w-19 overflow-hidden">
        {avatarImageObject.isUnder13 || !avatarImageObject.isExists ? (
          <ResponsiveImage
            alt={t('yourAvatar')}
            height={410}
            imageWidths="300"
            srcKey={avatarImageObject.url}
            width={318}
          />
        ) : (
          <ResponsiveImage
            alt={t('yourAvatar')}
            className="h-full overflow-hidden"
            height={318}
            imageClassName={`object-cover object-top scale-[3] p-3 ${
              metadata?.outfitGender === OutfitGender.MASCULINE ? 'mt-9' : 'mt-5'
            }`}
            loading="eager"
            src={avatarImageObject.url}
            width={318}
          />
        )}
      </div>
      <Link
        className="-right-2 -bottom-2 z-40 absolute h-10 hover:scale-110 duration-300 cursor-pointer transform"
        to={ROUTES.AVATAR}
      >
        <ResponsiveImage
          alt={t('editAvatar')}
          height={60}
          imageClassName="bg-purple-dark rounded-full py-1 pl-1 pr-0.5 border-2 border-white w-8"
          srcKey="icons/edit-avatar.svg"
          width={70}
        />
      </Link>
    </div>
  )
}
